import React, { useState } from 'react';
import {
    Card,
    CardContent,
    CardActions,
    CardMedia,
    Chip,
    Container,
    IconButton,
    Typography,
    TextField,
    Box,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { RoadmapItem } from '@/models/RoadmapItem';
import { STATUS } from '@/constants/status';
import { getColorPaletteForStatus } from '@/utils/color-utils';
import ImageVideo from './ImageVideo';
import StatusChip from './StatusChip';
import LikeButton from './LikeButton';
import FollowButton from './FollowButton';
import cwLogo from '@/assets/cw_logo.svg';
import cwLogoDark from '@/assets/cwlogo_dark.svg';
import cwLogoLight from '@/assets/cwlogo_light.svg';
import releaseNotes from '@/assets/release_notes.svg';
import './Flyout.css';

interface FlyoutProps {
    data: RoadmapItem;
    onClose: () => void;
    onLike: (jiraId: string, isLiked: boolean) => void;
    onFollow: (jiraId: string, isFollowed: boolean) => void;
    isAuthenticated?: boolean;
}

const Flyout: React.FC<FlyoutProps> = ({
    data,
    onClose,
    onLike,
    onFollow,
    isAuthenticated = true,
}) => {
    const [copyError, setCopyError] = useState(false);
    const [fallbackLink, setFallbackLink] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);

    const logoSrc = data.statusOnUI === STATUS.NEW_RELEASE ? cwLogoDark : cwLogoLight;
    const color = getColorPaletteForStatus(data.statusOnUI);

    const createPath = (): string => {
        const path = document.referrer === ''
            ? `${window.location.href}?details=${data.jiraId}`
            : `${document.referrer}?details=${data.jiraId}`;
        return path;
    };

    const handleCommunityClick = () => {
        window.open(data.virtualCommunity, '_blank');
    };

    const handleReleaseNotesClick = () => {
        window.open(data.releaseNotes, '_blank');
    };

    const copyLink = async () => {
        try {
            if (!navigator.clipboard) {
                throw new Error('Clipboard API not supported');
            }
            const path = createPath();
            await navigator.clipboard.writeText(path);
            setCopyError(false);
            setFallbackLink('');
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 3000);
        } catch (error) {
            console.error('Copy roadmap link failed:', error);
            const path = createPath();
            setCopyError(true);
            setFallbackLink(path);
        }
    };

    const handleEmailClick = () => {
        const subject = encodeURIComponent(`Check out: ${data.title}`);
        const body = encodeURIComponent(`I wanted to share this roadmap item with you:\n\n${data.title}\n\n${createPath()}`);
        window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    };

    const description = data.description
        .split(/[#*]/)
        .filter(Boolean)
        .map(item => item.trim());

    const getMediaUrl = (videoId: string | null): string => {
        if (videoId) return `https://img.youtube.com/vi/${videoId}/0.jpg`;
        return data.videoOrImageLink || logoSrc;
    };

    const showCommunityLink = data.virtualCommunity !== '' &&
        data.virtualCommunity.startsWith('https://virtualcommunity.connectwise.com');

    return (
        <ImageVideo videoUrl={data.videoOrImageLink}>
            {({ handleOpen, videoId }) => (
                <Card className="flyout-card">
                    <Box className="flyout-media-section">
                        <Box className="flyout-media-wrapper">
                            <CardMedia
                                component="img"
                                image={getMediaUrl(videoId)}
                                alt={videoId ? 'video thumbnail' : 'image'}
                                className={`flyout-media flyout-media-${color}`}
                                onClick={videoId ? handleOpen : undefined}
                                style={{
                                    cursor: videoId ? 'pointer' : 'default',
                                    objectFit: data.videoOrImageLink ? 'fill' : 'none',
                                }}
                            />
                            {videoId && (
                                <PlayCircleIcon
                                    className="flyout-play-icon"
                                    onClick={handleOpen}
                                />
                            )}
                        </Box>
                        <Box className="flyout-actions-column">
                            <Container className="flyout-action-buttons">
                                <IconButton
                                    aria-label="close"
                                    onClick={onClose}
                                    className="flyout-action-button"
                                >
                                    <CloseOutlinedIcon className="flyout-action-icon" />
                                </IconButton>
                                <IconButton
                                    aria-label="email"
                                    onClick={handleEmailClick}
                                    className="flyout-action-button"
                                >
                                    <EmailOutlinedIcon className="flyout-action-icon flyout-action-icon-colored" />
                                </IconButton>
                                <IconButton
                                    aria-label="copyLink"
                                    onClick={copyLink}
                                    className="flyout-action-button"
                                >
                                    <InsertLinkOutlinedIcon className="flyout-action-icon flyout-action-icon-colored" />
                                </IconButton>
                                {copySuccess && (
                                    <span className="flyout-copy-success">Copied!</span>
                                )}
                            </Container>
                        </Box>
                    </Box>

                    <CardContent>
                        <Container className="flyout-header">
                            <Typography variant="h6" className="flyout-category">
                                {data.category}
                            </Typography>
                            <span className="flyout-status">
                                <StatusChip data={{ ...data, color }} chipSize="medium" />
                            </span>
                        </Container>

                        <Typography variant="h6" className="flyout-title">
                            {data.title}
                        </Typography>

                        {copyError && (
                            <Box className="flyout-copy-fallback">
                                <Typography variant="body2" className="flyout-copy-label">
                                    Copy this link:
                                </Typography>
                                <TextField
                                    value={fallbackLink}
                                    fullWidth
                                    size="small"
                                    slotProps={{
                                        input: { readOnly: true }
                                    }}
                                    onClick={(e) => (e.target as HTMLInputElement).select()}
                                />
                            </Box>
                        )}

                        <Typography variant="body1" color="text.secondary" className="flyout-description">
                            <ul className="flyout-description-list">
                                {description.map((item, index) => (
                                    <li key={index}>{item}</li>
                                ))}
                            </ul>
                        </Typography>
                    </CardContent>

                    <CardActions className="flyout-footer">
                        {isAuthenticated && (
                            <Box className="flyout-footer-item">
                                <LikeButton data={data} onLike={onLike} variant="flyout" />
                            </Box>
                        )}
                        {isAuthenticated && (
                            <Box className="flyout-footer-item flyout-footer-follow">
                                <FollowButton data={data} onFollow={onFollow} variant="flyout" />
                            </Box>
                        )}
                        {showCommunityLink && (
                            <Box className="flyout-footer-item flyout-footer-link">
                                <img src={cwLogo} alt="ConnectWise" className="flyout-link-icon" />
                                <div className="flyout-link-text" onClick={handleCommunityClick}>
                                    Community
                                </div>
                            </Box>
                        )}
                        {data.releaseNotes !== '' && (
                            <Box className="flyout-footer-item flyout-footer-link">
                                <img src={releaseNotes} alt="Release Notes" className="flyout-link-icon-small" />
                                <div className="flyout-link-text" onClick={handleReleaseNotesClick}>
                                    Release Notes
                                </div>
                            </Box>
                        )}
                        <Container className="flyout-product-container">
                            <Chip
                                label={data.product}
                                size="small"
                                className="flyout-product-chip"
                            />
                        </Container>
                    </CardActions>
                </Card>
            )}
        </ImageVideo>
    );
};

export default Flyout;
